import { useDispatch } from "react-redux";
import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { buildQueryParams } from "../utils/utils";
import { useAuthContext } from "./use-auth-context.hook";
import { User } from "../models/user.model";

export const useSpacesApi = () => {
  const { sendAuthGuardedRequest } = useAuthContext();

  const findAllSpaces = async (
    limit?: number,
    offset?: number,
    vendorId?: string,
  ): Promise<any> => {
    const queryString = buildQueryParams([
      { key: "limit", value: limit?.toString()??"600" },
      { key: "offset", value: offset?.toString()??"0" },
      ...(vendorId ? [{ key: "vendorId", value: vendorId }] : []),
    ]);

    const response: any = await sendAuthGuardedRequest(
      ApiMethod.GET,
      routes.spaces.findAll + queryString,
    )
    return response
  };

  const findOneSpace = async (id): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.GET,
      routes.spaces.findOne(id),
    )
  };

  const findOneSpaceAndDelete = async (id: string): Promise<any> => {
    await sendAuthGuardedRequest(
      ApiMethod.DELETE,
      routes.spaces.deleteOne(id)
    )
  };

  /** Mark a library room as given to every new demo prospect (or not). Super admin only. */
  const setDemoDefault = async (id: string, value: boolean): Promise<{ id: string; isDemoDefault: boolean }> =>
    sendAuthGuardedRequest(ApiMethod.PUT, routes.spaces.demoDefault(id), { value }) as Promise<{ id: string; isDemoDefault: boolean }>;

  const createSpace = async (space): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.POST,
      routes.spaces.create,
      space
    )
  };

  const updateSpace = async (space): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.PATCH,
      routes.spaces.patch,
      space
    )
  };

  const runAnalysis = async (spaceFile): Promise<any> => {
    let formData = new FormData();
    formData.append('image', spaceFile);

    return await sendAuthGuardedRequest(
      ApiMethod.POST,
      routes.spaces.analysis,
      formData,
      {
        headers: { 'Device-Token': "111" }
      }
    )
  };

  const updateMasks = async (spaceId, maskFile): Promise<any> => {
    let formData = new FormData();
    formData.append('mask', maskFile);

    return await sendAuthGuardedRequest(
      ApiMethod.PATCH,
      routes.spaces.patchMasks(spaceId),
      maskFile
    )
  };

  /**
   * Upload a room photo through the same analysis pipeline as the visualizer.
   * Returns { jobId }; poll getAnalysisStatus until completed. The space is
   * created hidden (uploadedByVisitor) until it is published from the Space
   * Manager's deploy step.
   */
  const analyzeSpace = async (file: File): Promise<{ jobId: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', 'admin');
    return (await sendAuthGuardedRequest(ApiMethod.POST, routes.spaces.analyzeAsync, formData)) as { jobId: string }
  };

  const getAnalysisStatus = async (jobId: string): Promise<{
    status: string; progress?: number; message?: string; error?: string; result?: any
  }> => {
    return (await sendAuthGuardedRequest(ApiMethod.GET, routes.spaces.analysisStatus(jobId))) as any
  };

  /** Replaces every marker zone of a space; an empty list removes them. */
  const updateZones = async (spaceId: string, zones: any[]): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.PUT,
      routes.spaces.putZones(spaceId),
      { zones }
    )
  };

  const findAllSpaceTypes = async (
    limit?: number,
    offset?: number,
  ): Promise<any> => {
    const queryString = buildQueryParams([
      { key: "limit", value: limit?.toString() },
      { key: "offset", value: offset?.toString() },
    ]);

    const response: any = await sendAuthGuardedRequest(
      ApiMethod.GET,
      routes.spaces.findAllSpaceTypes + queryString,
    )
    return response
  };

  const assignSpaces = async (spaces): Promise<any> => {
    return await sendAuthGuardedRequest(
      ApiMethod.POST,
      routes.spaces.assignSpaces,
      spaces.map(space => space.id)
    )
  };


  return {
    findAllSpaces,
    findOneSpace,
    findOneSpaceAndDelete,
    setDemoDefault,
    createSpace,
    updateSpace,
    runAnalysis,
    updateMasks,
    updateZones,
    analyzeSpace,
    getAnalysisStatus,
    findAllSpaceTypes,
    assignSpaces
  };

};
